interface PipelineFieldIdentity {
  name: string;
}

interface PipelineSectionState {
  original?: string;
  awaitingChoice?: boolean;
}

/** Checkpoints and empty image slots are not a terminal run status. */
export function isTextGenerationComplete(target: {
  status?: string;
  isComplete?: boolean;
  sections?: Record<string, unknown>;
}): boolean {
  const state = target.sections?.__pipeline_state;
  if (isRecord(state) && ['generating', 'cancelled', 'error'].includes(String(state.original))) return false;
  return target.isComplete === true || target.status === 'COMPLETE';
}

export function completedProductImageNames(
  images: Array<{ kind?: string; driveFileId?: string | null }>,
  fields: PipelineFieldIdentity[],
): string[] {
  const fieldNames = new Set(fields.map(field => field.name));
  return [...new Set(images
    .filter(image => image.driveFileId?.trim() && image.kind && fieldNames.has(image.kind))
    .map(image => image.kind!))];
}

interface ProductImagesRunSection {
  original?: string;
  runId?: string;
  message?: string;
  totalFields?: number;
  completedFields?: number;
  editedAt?: string;
}

interface ProductImagesRunTarget {
  sections?: Record<string, unknown>;
}

export interface ProductImagesRunState {
  status: 'preparing' | 'generating' | 'completed' | 'error';
  runId: string;
  message?: string;
  totalFields?: number;
  completedFields?: number;
  updatedAt?: string;
}

const PRODUCT_IMAGES_RUN_SECTION = '__product_images_run';
const PRODUCT_IMAGES_STALE_AFTER_MS = 2 * 60 * 60 * 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getProductImagesRunState(target?: ProductImagesRunTarget | null): ProductImagesRunState | null {
  const value = target?.sections?.[PRODUCT_IMAGES_RUN_SECTION];
  if (!isRecord(value)) return null;
  const section = value as ProductImagesRunSection;
  if (!['preparing', 'generating', 'completed', 'error'].includes(section.original || '')) return null;
  if (!section.runId) return null;
  return {
    status: section.original as ProductImagesRunState['status'],
    runId: section.runId,
    message: section.message,
    totalFields: section.totalFields,
    completedFields: section.completedFields,
    updatedAt: section.editedAt,
  };
}

export function getProductImagesRunFailure(
  target?: ProductImagesRunTarget | null,
  now = Date.now(),
): string | null {
  const state = getProductImagesRunState(target);
  if (!state) return null;
  if (state.status === 'error') {
    return state.message || 'Generowanie zdjęć produktowych zostało przerwane. Spróbuj ponownie.';
  }
  if (state.status !== 'preparing' && state.status !== 'generating') return null;
  const updatedAt = state.updatedAt ? Date.parse(state.updatedAt) : Number.NaN;
  if (Number.isFinite(updatedAt) && now - updatedAt >= PRODUCT_IMAGES_STALE_AFTER_MS) {
    return 'Generowanie zdjęć produktowych nie odpowiada od ponad 2 godzin. Uruchom je ponownie.';
  }
  return null;
}

interface ProgressEventIdentity {
  labProductId: string;
  pipeline?: string;
  targetId?: string;
}

interface ExpectedRunIdentity {
  labProductId: string;
  pipeline: string;
  targetId?: string | null;
}

export function firstIncompletePipelineStep(
  fields: PipelineFieldIdentity[],
  sections: Record<string, PipelineSectionState>,
): string | undefined {
  return fields.find(field => (
    sections[field.name]?.awaitingChoice || !sections[field.name]?.original?.trim()
  ))?.name;
}

export function isProgressEventForRun(
  event: ProgressEventIdentity,
  expected: ExpectedRunIdentity,
): boolean {
  if (event.labProductId !== expected.labProductId) return false;
  if (event.pipeline && event.pipeline !== expected.pipeline) return false;
  if (!event.targetId) return true;
  return Boolean(expected.targetId) && event.targetId === expected.targetId;
}

export function selectGenerationKnowledgeBase<T extends { id?: string; isComplete?: boolean }>(
  knowledgeBases: T[],
  activeId?: string | null,
): T | undefined {
  if (activeId) {
    const active = knowledgeBases.find(item => item.id === activeId);
    if (active) return active;
  }
  return knowledgeBases.find(item => !item.isComplete);
}

export function centeredChecklistScrollTop(metrics: {
  currentScrollTop: number;
  containerTop: number;
  containerHeight: number;
  itemTop: number;
  itemHeight: number;
  scrollHeight: number;
}): number {
  const relativeItemCenter = metrics.itemTop - metrics.containerTop + (metrics.itemHeight / 2);
  const desiredTop = metrics.currentScrollTop + relativeItemCenter - (metrics.containerHeight / 2);
  return Math.min(
    Math.max(0, metrics.scrollHeight - metrics.containerHeight),
    Math.max(0, desiredTop),
  );
}

/** The pipeline ranks option 1 first; a marked recommendation takes precedence. */
export function automaticGenerationChoice(options: string[], cards: Array<{ recommended: boolean }>): string | null {
  const recommendedIndex = cards.findIndex(card => card.recommended);
  return options[recommendedIndex >= 0 ? recommendedIndex : 0] || null;
}
